/**
 * BrainProgressBadge — progress that communicates confidence, not just a percentage.
 *
 * Renders the phase label ("Foundation Established"), the percentage,
 * and a subtle gradient bar. Used inside the MissionShell body header.
 */
import { milestoneFor } from "@/entities/brain-missions";
import type { BrainMissionType } from "@/entities/brain-missions";

interface BrainProgressBadgeProps {
  type: BrainMissionType;
  answered: number;
  total: number;
}

export function BrainProgressBadge({ type, answered, total }: BrainProgressBadgeProps) {
  const m = milestoneFor(type, answered, total);
  const label = type === "business_brain" ? "Business Brain" : "Brand Brain";
  return (
    <div
      className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3"
      data-testid={`brain-progress-badge-${type}`}
      data-percent={m.percent}
      data-phase={m.phase}
    >
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-white/50">
            {label}
          </div>
          <div className="mt-0.5 text-sm font-semibold text-white">{m.label}</div>
        </div>
        <div
          className="text-right text-2xl font-semibold tabular-nums text-white"
          data-testid="brain-progress-percent"
        >
          {m.percent}%
        </div>
      </div>
      <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
        <div
          className="h-full rounded-full bg-gradient-to-r from-cyan-400 via-sky-500 to-violet-500 transition-[width] duration-500"
          style={{ width: `${m.percent}%` }}
        />
      </div>
      <div className="mt-2 text-xs text-white/60">{m.detail}</div>
    </div>
  );
}
